import {
  AppBar,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { IUser } from '../../shared-interfaces/User/IUser';
import CookieService from '../../shared-services/CookieService';
import '../../styles/cl.topbar.scss';
import LaunchDarklyContext from '../contexts/LaunchDarklyContext';
import AnthologyLogo from './AnthologyLogo';
import ProductName from './ProductName';

// xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve"

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  toolBar: {
    minHeight: '48px',
    paddingLeft: 0,
  },
  topBarContainer: {
    backgroundColor: '#333',
    fontFamily: 'OpenSans, Helvetica, Arial, sans-serif',
    fontSize: '16px',
    height: '48px',
    zIndex: 1000,
  },
  topBarLogoContainer: {
    height: '48px',
    padding: 0,
    flexGrow: 0,
    borderRight: '1px solid #666',
    backgroundColor: '#000',
  },
  topBarLogo: {
    width: '44px',
    height: '48px',
    '&:hover, &:focus': {
      backgroundColor: '#222',
      borderLeft: '1px solid #888',
      borderRight: '1px solid #888',
      borderTop: '1px solid #888',
      borderBottom: '1px solid #000',
    },
  },
  topBarProductContainer: {
    flexGrow: 1,
    height: '48px',
  },
  topBarProduct: {
    height: '48px',
    padding: '0px 14px 0px 14px',
    border: '1px solid #333',
    '&:hover, &:focus': {
      backgroundColor: '#454545',
      borderRight: '1px solid #555',
      borderLeft: '1px solid #888',
      borderTop: '1px solid #555',
      borderBottom: '1px solid #222',
    },
  },

  userMenuButtonContainer: {
    zIndex: 20,
    '&:hover': {
      backgroundColor: '#454545',
    },
  },
  userMenu: {
    '& $ul': {
      borderLeft: '5px solid #662D91',
      backgroundColor: '#454545',
      padding: 0,
    },
  },
  userMenuButton: {
    flexGrow: 0,
    background: 'transparent',
    height: '48px',
    borderRadius: 0,
    color: '$fff',
    zIndex: 20,
    '&:hover': {
      backgroundColor: '#454545',
    },
  },
  userMenuButtonName: {
    fontSize: '14px',
    textTransform: 'none',
    color: '#fff',
  },
  userMenuAvatar: {
    display: 'inline-flex',
    color: '#000',
    fontSize: '20px',
    fontWeight: 'bold',
    height: '60px',
    lineHeight: '60px',
    margin: '0 auto 8px',
    textAlign: 'center',
    width: '60px',
  },
  userMenuDetails: {
    backgroundColor: '#fff',
    width: '300px',
    textAlign: 'center',
    padding: '20px 0',
  },
  userMenuItem: {
    backgroundColor: '#454545',
    padding: '12px 16px',
    '&:hover': {
      backgroundColor: '#000000',
      color: '#fff',
    },
  },
  userMenuItemLink: {
    color: '#fff',
    textDecoration: 'none',
  },
}));

interface AnthologyTopBarProps {
  user: IUser;
  federationProvider: string;
  homepage: string;
}

const AnthologyTopBar = (props: AnthologyTopBarProps) => {
  const launchDarkly = useContext(LaunchDarklyContext);

  // We will get rid of this down the road
  const beaconHomeOptOutCookie = !launchDarkly['home-can-opt-out']
    ? false
    : CookieService.Get('BeaconHomeOptOut') === 'true';

  const classes = useStyles();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const initials = () => {
    let ret = '';
    if (props.user.firstName && props.user.firstName.length > 0)
      ret += props.user.firstName.charAt(0);
    if (props.user.lastName && props.user.lastName.length > 0)
      ret += props.user.lastName.charAt(0);
    return ret;
  };

  return (
    <div className={classes.root}>
      <AppBar className={classes.topBarContainer} elevation={0}>
        <Toolbar className={classes.toolBar} disableGutters>
          <a
            href={window.location.origin}
            id="topBar-homepage-link"
            title="More from Anthology"
            role="link"
            className={classes.topBarLogoContainer}
          >
            <AnthologyLogo className={classes.topBarLogo} />
          </a>
          {launchDarkly['network-page'] && !beaconHomeOptOutCookie ? (
            <Link
              to="/"
              title="Beacon Home"
              className={classes.topBarProductContainer}
            >
              <ProductName className={classes.topBarProduct} />
            </Link>
          ) : (
            <a
              href="/beacon"
              title="Beacon Home"
              className={classes.topBarProductContainer}
            >
              <ProductName className={classes.topBarProduct} />
            </a>
          )}

          <Button
            id="topBar-toggle-button"
            variant="text"
            className={classes.userMenuButton}
            onClick={(e: any) => setMenuAnchor(e.currentTarget)}
          >
            <span
              id="user-initials"
              className="topBar-toggle-initials"
              role="presentation"
              aria-hidden="true"
              data-clarity-mask="true"
            >
              {initials()}
            </span>
            <span id="user-firstName">
              <Typography
                className={classes.userMenuButtonName}
                aria-label="Account Information for"
                data-clarity-mask="true"
              >
                {props.user.firstName}
              </Typography>
            </span>
          </Button>
        </Toolbar>
      </AppBar>
      <Menu
        id="user-menu"
        className={classes.userMenu}
        anchorEl={menuAnchor}
        keepMounted
        PaperProps={{ square: true }}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
        }}
      >
        <li className={classes.userMenuDetails}>
          <div>
            <Avatar className={classes.userMenuAvatar} data-clarity-mask="true">
              {initials()}
            </Avatar>
          </div>
          <div
            id="topBar-fullName"
            className="topBar-fullName"
            data-clarity-mask="true"
          >
            {`${props.user.firstName} ${props.user.lastName}`}
          </div>
          <div
            id="topBar-institution"
            className="topBar-institution"
            data-clarity-mask="true"
          >
            {props.user.institutionName}
          </div>
        </li>
        <MenuItem className={classes.userMenuItem}>
          <a
            href="/beacon/students/me/profile"
            id="topBar-editProfile"
            className={classes.userMenuItemLink}
            role="menuitem"
          >
            Profile
          </a>
        </MenuItem>
        <MenuItem className={classes.userMenuItem}>
          <a
            href="https://blackboard.my.site.com/Support/s/topic/0TOPU00000Ao1ch4AB/anthology-beacon?language=en_US"
            target="_blank"
            id="topBar-help"
            className={classes.userMenuItemLink}
            role="menuitem"
            rel="noreferrer"
          >
            Support
          </a>
        </MenuItem>
        <MenuItem className={classes.userMenuItem}>
          <a
            href={`${props.federationProvider}/?wa=wsignout1.0`}
            id="topBar-signOut"
            className={classes.userMenuItemLink}
            role="menuitem"
          >
            Sign Out
          </a>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default AnthologyTopBar;
